import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Check, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('monthly');

  const packages = {
    weekly: {
      price: 9.99,
      label: 'Weekly',
      description: 'Perfect for trying premium features',
      savings: null
    },
    monthly: {
      price: 29.99,
      label: 'Monthly',
      description: 'Best value for serious dating',
      savings: 'Save 25%'
    }
  };

  const handleUpgrade = async (packageType) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/payment/create-checkout`,
        { package_type: packageType },
        { withCredentials: true }
      );

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to create checkout session');
      setLoading(false);
    }
  };

  const isPremium = user?.user?.subscription_status === 'premium';

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <header className="glass border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isPremium ? (
          <div className="card text-center py-12">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">You're Premium!</h2>
            <p className="text-gray-600 mb-2">Enjoy all the benefits of Soundlyke Premium</p>
            {user?.user?.subscription_expires_at && (
              <p className="text-sm text-gray-500">
                Expires: {new Date(user.user.subscription_expires_at).toLocaleDateString()}
              </p>
            )}
            <button
              onClick={() => navigate('/')}
              className="btn-primary mt-6"
            >
              Back to Dashboard
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-block p-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl mb-4">
                <Crown className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Upgrade to Premium</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Unlock the full Soundlyke experience and see who you're really connecting with
              </p>
            </div>

            {/* Benefits */}
            <div className="card mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Premium Benefits</h3>
              <div className="space-y-3">
                {[
                  'View profile photos of your matches',
                  'Send unlimited messages',
                  'Priority customer support',
                  'See who liked your profile'
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {Object.entries(packages).map(([key, pkg]) => (
                <div
                  key={key}
                  onClick={() => setSelectedPackage(key)}
                  className={`card cursor-pointer transition-all ${
                    selectedPackage === key
                      ? 'ring-4 ring-pink-500 bg-gradient-to-br from-pink-50 to-purple-50'
                      : 'hover:shadow-xl'
                  }`}
                  data-testid={`package-${key}`}
                >
                  {pkg.savings && (
                    <div className="absolute top-4 right-4">
                      <div className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        {pkg.savings}
                      </div>
                    </div>
                  )}
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.label}</h3>
                    <div className="text-4xl font-bold gradient-text mb-2">
                      ${pkg.price}
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
                    {selectedPackage === key && (
                      <div className="flex items-center justify-center space-x-2 text-pink-500 font-semibold">
                        <Sparkles className="w-5 h-5" />
                        <span>Selected</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button
              onClick={() => handleUpgrade(selectedPackage)}
              disabled={loading}
              className="w-full btn-primary py-5 text-lg flex items-center justify-center space-x-3"
              data-testid="checkout-btn"
            >
              {loading ? (
                <>
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Crown className="w-6 h-6" />
                  <span>Continue to Checkout</span>
                </>
              )}
            </button>

            {/* Note */}
            <p className="text-center text-sm text-gray-500 mt-6">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default PaymentPage;